import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import api from '../../services/api';
import ColumnSettingsMenu from './ColumnSettingsMenu';
import SectionCard from './SectionCard';
import QuestionTable from './QuestionTable';
import TemplatePreviewModal from './TemplatePreviewModal';
import {
  DEFAULT_VISIBLE_COLUMNS,
  COLUMN_TO_SEGMENT,
  emptySection,
  emptyStructure,
  computeTotals,
  validateStructureMarks,
} from './templateSchema';
import { confirmAction } from '../../utils/confirm';

/**
 * Full-screen editor for creating / editing a QP Template.
 * Modes:
 *   - editingTemplate === null  -> create
 *   - editingTemplate is set    -> load and edit
 *
 * On mount, loads master values for every optional attribute column in
 * parallel so inline attribute cells render as dropdowns populated from
 * the user-managed QP Master Values list.
 */
function TemplateEditor({ tenantId, templateId, onClose, onSaved }) {
  const isEdit = !!templateId;

  // Top-level metadata
  const [templateName, setTemplateName] = useState('');
  const [description, setDescription] = useState('');
  const [maxMarks, setMaxMarks] = useState('');
  const [durationMinutes, setDurationMinutes] = useState('');
  const [instructions, setInstructions] = useState('');

  // Visible columns + structure
  const [visibleColumns, setVisibleColumns] = useState(DEFAULT_VISIBLE_COLUMNS);
  const [structure, setStructure] = useState(emptyStructure());

  // Master-value dropdown options keyed by column key (co, unit, bl, ...).
  const [options, setOptions] = useState({});

  // UI state
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [showColMenu, setShowColMenu] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // ----- Load master values for all optional columns -----
  useEffect(() => {
    if (!tenantId) return;
    (async () => {
      try {
        const entries = Object.entries(COLUMN_TO_SEGMENT);
        const results = await Promise.all(
          entries.map(([, segmentCode]) =>
            api
              .get(`/qp-settings/master-values/${tenantId}`, {
                params: { segmentCode },
              })
              .then((r) => (r.data?.success ? r.data.data || [] : []))
              .catch(() => [])
          )
        );
        const opts = {};
        entries.forEach(([colKey], i) => {
          opts[colKey] = (results[i] || [])
            .filter((v) => v.IsActive)
            .map((v) => v.ValueName);
        });
        setOptions(opts);
      } catch {
        // Non-fatal: dropdowns will fall back to free-text inputs.
      }
    })();
  }, [tenantId]);

  // ----- Load template when editing -----
  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        setLoading(true);
        const res = await api.get(`/qp-templates/detail/${templateId}`);
        if (res.data.success) {
          const t = res.data.data;
          setTemplateName(t.TemplateName || '');
          setDescription(t.Description || '');
          setMaxMarks(t.MaxMarks ?? '');
          setDurationMinutes(t.DurationMinutes ?? '');
          setInstructions(t.Instructions || '');
          setVisibleColumns(
            Array.isArray(t.VisibleColumns) && t.VisibleColumns.length > 0
              ? t.VisibleColumns
              : DEFAULT_VISIBLE_COLUMNS
          );
          // Normalize legacy documents that only had `sections`.
          const loaded = t.StructureJSON && typeof t.StructureJSON === 'object' ? t.StructureJSON : {};
          setStructure({
            questions: Array.isArray(loaded.questions) ? loaded.questions : [],
            sections: Array.isArray(loaded.sections) ? loaded.sections : [],
          });
        }
      } catch {
        toast.error('Failed to load template');
      } finally {
        setLoading(false);
      }
    })();
  }, [isEdit, templateId]);

  const totals = useMemo(() => computeTotals(structure), [structure]);

  // ----- Direct (section-less) questions -----
  const setDirectQuestions = (qs) => {
    setStructure((prev) => ({ ...prev, questions: qs }));
  };

  // ----- Section helpers -----
  const addSection = () => {
    const nextLabel = String.fromCharCode(65 + (structure.sections?.length || 0));
    const sec = emptySection(nextLabel);
    setStructure((prev) => ({ ...prev, sections: [...(prev.sections || []), sec] }));
  };

  const updateSection = (idx, updated) => {
    setStructure((prev) => {
      const next = [...(prev.sections || [])];
      next[idx] = updated;
      return { ...prev, sections: next };
    });
  };

  const deleteSection = async (idx) => {
    const sec = structure.sections[idx];
    const ok = await confirmAction(
      `Delete ${sec.name || 'Section'}?`,
      'All questions in this section will be removed.',
      { confirmText: 'Yes, delete', icon: 'warning' }
    );
    if (!ok) return;
    setStructure((prev) => {
      const next = [...(prev.sections || [])];
      next.splice(idx, 1);
      return { ...prev, sections: next };
    });
  };

  // ----- Save -----
  const handleSave = async () => {
    if (!templateName.trim()) return toast.error('Template Name is required');

    // Marks validation: block save if any parent disagrees with its sub-questions.
    const errors = validateStructureMarks(structure);
    if (errors.length > 0) {
      const preview = errors.slice(0, 3).join(' | ');
      const more = errors.length > 3 ? ` (+${errors.length - 3} more)` : '';
      toast.error(`Marks mismatch: ${preview}${more}`);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        tenantId,
        templateName: templateName.trim(),
        description: description.trim() || null,
        maxMarks: Number(maxMarks) || 0,
        durationMinutes: durationMinutes ? parseInt(durationMinutes) : null,
        instructions: instructions.trim() || null,
        visibleColumns,
        structure,
      };

      if (isEdit) {
        await api.put(`/qp-templates/${templateId}`, payload);
        toast.success('Template updated');
      } else {
        await api.post('/qp-templates', payload);
        toast.success('Template created');
      }
      onSaved && onSaved();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="qp-editor-page">
        <div style={{ textAlign: 'center', padding: 60, color: '#999' }}>Loading...</div>
      </div>
    );
  }

  const hasDirect = (structure.questions || []).length > 0;
  const hasSections = (structure.sections || []).length > 0;

  return (
    <div className="qp-editor-page">
      {/* Top bar */}
      <div className="qp-editor-topbar">
        <div className="qp-editor-topbar-left">
          <button className="qp-icon-btn" onClick={onClose} title="Back">◀</button>
          <h2 className="qp-editor-title">
            {isEdit ? 'Edit Template' : 'Create Template'}
          </h2>
        </div>
        <div className="qp-editor-topbar-right">
          <div
            className={
              'qp-stat-pill' +
              (Number(maxMarks) > 0 && totals.setMarks !== Number(maxMarks)
                ? ' qp-stat-pill-warn'
                : '')
            }
            title={
              Number(maxMarks) > 0
                ? `Attempt: ${totals.attemptMarks}  •  Set: ${totals.setMarks}  •  Target (Max Marks): ${maxMarks}`
                : 'Attempt marks (OR options collapsed to max) / Set marks (sum of all questions)'
            }
          >
            <span className="qp-stat-pill-label">Max Marks</span>
            <span className="qp-stat-pill-value">
              {totals.attemptMarks}/{totals.setMarks}
            </span>
          </div>
          <div className="qp-stat-pill">
            <span className="qp-stat-pill-label">Questions</span>
            <span className="qp-stat-pill-value">{totals.totalQuestions}</span>
          </div>
          <div className="qp-stat-pill">
            <span className="qp-stat-pill-label">Sections</span>
            <span className="qp-stat-pill-value">{totals.totalSections}</span>
          </div>
          <button className="btn btn-outline btn-sm" onClick={() => setShowPreview(true)}>
            👁 Preview
          </button>
          <div className="qp-col-settings-wrap">
            <button
              className="qp-icon-btn qp-settings-icon"
              title="Column settings"
              onClick={() => setShowColMenu((v) => !v)}
            >
              ⚙
            </button>
            {showColMenu && (
              <ColumnSettingsMenu
                visibleColumns={visibleColumns}
                onChange={setVisibleColumns}
                onClose={() => setShowColMenu(false)}
              />
            )}
          </div>
          <button className="btn btn-save btn-sm" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Template'}
          </button>
        </div>
      </div>

      {/* Metadata card */}
      <div className="qp-editor-body">
        <div className="qp-meta-card">
          <div className="qp-meta-grid">
            <div className="form-group" style={{ flex: 2, marginBottom: 0 }}>
              <label className="required-label">Template Name</label>
              <input
                type="text"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="e.g., MAHER Pattern #1 – 50 Marks"
              />
            </div>
            <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
              <label>Max Marks</label>
              <input
                type="number"
                min="0"
                value={maxMarks}
                onChange={(e) => setMaxMarks(e.target.value)}
                placeholder="100"
              />
            </div>
            <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
              <label>Duration (minutes)</label>
              <input
                type="number"
                min="0"
                value={durationMinutes}
                onChange={(e) => setDurationMinutes(e.target.value)}
                placeholder="180"
              />
            </div>
          </div>
          <div className="form-group" style={{ marginBottom: 0, marginTop: 12 }}>
            <label>Description</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Short note for internal reference"
            />
          </div>
          <div className="form-group" style={{ marginBottom: 0, marginTop: 12 }}>
            <label>Instructions to Candidates</label>
            <textarea
              rows={2}
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              placeholder="e.g., All questions are compulsory. Answer any 5 of 7 from Section B."
              style={{ width: '100%', resize: 'vertical' }}
            />
          </div>
        </div>

        {/* Sections & Questions */}
        <div className="qp-sections-header">
          <h3 style={{ margin: 0 }}>Sections &amp; Questions</h3>
          <p className="qp-sections-hint">
            Sections are optional — add questions directly below, or group them under sections.
            Use <strong>+</strong> to add sub-questions and <strong>+OR</strong> to create an
            internal-choice pair.
          </p>
        </div>

        {/* Direct (section-less) questions */}
        <div className="qp-direct-card">
          <div className="qp-direct-head">
            <span className="qp-direct-title">Direct Questions</span>
            <span className="qp-direct-hint">
              (No section — ideal for patterns without Part / Section grouping)
            </span>
          </div>
          <QuestionTable
            questions={structure.questions || []}
            visibleColumns={visibleColumns}
            options={options}
            onChange={setDirectQuestions}
            addLabel="+ Add Question"
            emptyText={
              hasSections && !hasDirect
                ? 'No direct questions. Add one here if your paper also has un-sectioned questions.'
                : 'No questions yet. Click below to add one.'
            }
          />
        </div>

        {(structure.sections || []).map((sec, si) => (
          <SectionCard
            key={sec.id}
            section={sec}
            index={si}
            visibleColumns={visibleColumns}
            options={options}
            onChange={(u) => updateSection(si, u)}
            onDelete={() => deleteSection(si)}
          />
        ))}

        <button className="qp-add-section-btn" onClick={addSection}>
          + Add New Section
        </button>
      </div>

      {showPreview && (
        <TemplatePreviewModal
          template={{ templateName, maxMarks, durationMinutes }}
          structure={structure}
          visibleColumns={visibleColumns}
          onClose={() => setShowPreview(false)}
        />
      )}
    </div>
  );
}

export default TemplateEditor;
